import type { ChatFileRef } from "./api";
import { apiFetch } from "./backend";
import { inTauri, registerToolSource, type Tool, type ToolContext } from "./tools";

type Invoke = typeof import("@tauri-apps/api/core").invoke;
type ServerChatFile = { id: string; name: string; mime: string; size: number; lines: number | null; preview: string | null };
type PreparedShare = { name: string; relative_path: string; size: number };

async function syncInbox(invoke: Invoke, chatId: string) {
  const response = await apiFetch(`/api/chats/${chatId}/files`);
  if (!response.ok) return;
  const files = ((await response.json()) as ServerChatFile[]);
  const missing = await invoke<string[]>("sandbox_inbox_missing", {
    chatId,
    files: files.map((f) => ({ name: f.name, size: f.size })),
  });
  for (const name of missing) {
    const file = files.find((f) => f.name === name)!;
    const download = await apiFetch(`/api/chats/${chatId}/files/${file.id}`);
    if (!download.ok) continue;
    await invoke("sandbox_inbox_write", new Uint8Array(await download.arrayBuffer()), {
      headers: { "x-chat-id": chatId, "x-file-name": encodeURIComponent(name) },
    });
  }
}

async function shareFile(invoke: Invoke, chatId: string, path: string): Promise<ChatFileRef> {
  const prepared = await invoke<PreparedShare>("sandbox_share_prepare", { args: { chat_id: chatId, path } });
  const bytes = await invoke<ArrayBuffer>("sandbox_read_file", { chatId, relativePath: prepared.relative_path });
  const form = new FormData();
  form.append("origin", "sandbox");
  form.append("file", new File([bytes], prepared.name));
  const response = await apiFetch(`/api/chats/${chatId}/files`, { method: "POST", body: form });
  if (!response.ok) throw new Error(`Couldn't add the file to the chat: ${await response.text()}`);
  const [saved] = (await response.json()) as ServerChatFile[];
  return { type: "file", id: saved.id, name: saved.name, mime: saved.mime, size: saved.size, lines: saved.lines, preview: saved.preview };
}

function requireChat(ctx: ToolContext): string {
  if (!ctx.chatId) throw new Error("The sandbox needs a chat to belong to.");
  return ctx.chatId;
}

registerToolSource(async () => {
  if (!inTauri) return [];
  const { invoke } = await import("@tauri-apps/api/core");

  const tools: Tool[] = [
    {
      source: "tauri",
      name: "sandbox_exec",
      description:
        "Run a bash command in this chat's private Alpine Linux sandbox. The home folder (/home/sandbox, also the working directory) " +
        "persists for this chat; everything else is reset when the sandbox is recreated. Has network access, python3, node, git, curl, jq. " +
        "Install Python or npm packages with pip/npm as usual. For system packages use sandbox_install instead of apk. " +
        "Files the user attached to this chat are in ~/inbox. " +
        "Returns exit_code, stdout, stderr.\n\n" +
        "Your context is small, so keep output small. Output over ~6 KB is saved to a file (stdout_full.file) and you only get " +
        "its first and last few KB; inspect that file with head, sed -n, grep, jq or python instead of printing it again. " +
        "Never print whole web pages or large files: save them first (curl -o page.html, webpage URL > page.md), check the size " +
        "with wc -c, then extract only what you need. `webpage URL` prints a page's main content as Markdown " +
        "(--full for the whole page, --links to keep URLs). For JSON APIs, filter with jq. " +
        "Never say you ran something or describe its output unless you actually called this tool.",
      requiresPermission: "subprocess",
      schema: {
        type: "object",
        properties: {
          command: { type: "string", description: "Bash command, e.g. 'python3 script.py' or 'curl -s https://example.com'." },
          timeout_secs: { type: "integer", description: "Kill the command after this many seconds (default 60, max 600)." },
        },
        required: ["command"],
        additionalProperties: false,
      },
      run: async (args, ctx) => {
        const command = String(args.command ?? "");
        if (!command.trim()) return { error: "empty command" };
        const chatId = requireChat(ctx);
        await syncInbox(invoke, chatId).catch((e) => console.warn("Inbox sync failed:", e));
        return invoke("sandbox_exec", { args: { chat_id: chatId, command, timeout_secs: args.timeout_secs } });
      },
    },
    {
      source: "tauri",
      name: "sandbox_install",
      description:
        "Install Alpine system packages (apk) in this chat's sandbox. Installed packages are remembered and reinstalled automatically " +
        "if the sandbox is recreated. Use package names only, e.g. ['ffmpeg', 'py3-numpy'].",
      requiresPermission: "subprocess",
      schema: {
        type: "object",
        properties: {
          packages: { type: "array", items: { type: "string" }, minItems: 1 },
        },
        required: ["packages"],
        additionalProperties: false,
      },
      run: async (args, ctx) => {
        const packages = Array.isArray(args.packages) ? args.packages.map(String) : [];
        return invoke("sandbox_install", { args: { chat_id: requireChat(ctx), packages } });
      },
    },
    {
      source: "tauri",
      name: "sandbox_reset",
      description:
        "Throw away this chat's sandbox container and start fresh on the next command. The home folder and the list of installed packages are kept.",
      requiresPermission: "subprocess",
      schema: { type: "object", properties: {}, additionalProperties: false },
      run: async (_args, ctx) => {
        await invoke("sandbox_reset", { chatId: requireChat(ctx) });
        return { ok: true };
      },
    },
    {
      source: "tauri",
      name: "sandbox_share",
      description:
        "Hand a file or folder from the sandbox to the user: it appears in the chat as a download. Folders are zipped. " +
        "Use this for results the user asked for (a converted document, a chart, a report); describe it in your reply too.",
      requiresPermission: "subprocess",
      schema: {
        type: "object",
        properties: {
          path: { type: "string", description: "Path inside the sandbox home, e.g. 'out/chart.png' or '/home/sandbox/report'." },
        },
        required: ["path"],
        additionalProperties: false,
      },
      run: async (args, ctx) => {
        const path = String(args.path ?? "").trim();
        if (!path) return { error: "no path given" };
        return { shared: await shareFile(invoke, requireChat(ctx), path) };
      },
    },
  ];
  return tools;
});
